import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Send, X } from "lucide-react";
import { adminApi } from "@/lib/lnlApi";
import RichTextEditor from "./RichTextEditor";
import { Modal, Field, errMsg, inputCls } from "./shared";

const MAX = 10 * 1024 * 1024;

export default function RespondComposer({ kind, record, recipient, onClose, onSent }) {
    const [meta, setMeta] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [sigs, setSigs] = useState([]);
    const [tplId, setTplId] = useState("");
    const [subject, setSubject] = useState(`Re: Your inquiry ${record.reference}`);
    const [body, setBody] = useState("");
    const [sig, setSig] = useState("");
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [editorKey, setEditorKey] = useState(0);

    useEffect(() => {
        adminApi.get("/auto-responses/meta").then((r) => setMeta(r.data)).catch(() => {});
        adminApi.get("/auto-responses/templates").then((r) => setTemplates(r.data.filter((t) => t.is_active))).catch(() => {});
        adminApi.get("/auto-responses/signatures").then((r) => setSigs(r.data)).catch(() => {});
    }, []);

    function applyTemplate(id) {
        setTplId(id);
        const t = templates.find((x) => x.id === id);
        if (t) { setSubject(t.subject); setBody(t.body); setSig(t.signature_id || ""); setEditorKey((k) => k + 1); }
    }
    function addFiles(list) {
        const next = [...files, ...Array.from(list)];
        if (next.reduce((s, f) => s + f.size, 0) > MAX) return toast.error("Attachments can’t exceed 10 MB in total.");
        setFiles(next);
    }
    async function send() {
        setBusy(true);
        const fd = new FormData();
        fd.append("subject", subject); fd.append("body", body); fd.append("template_id", tplId); fd.append("signature_id", sig);
        files.forEach((f) => fd.append("files", f));
        try { await adminApi.post(`/auto-responses/records/${kind}/${record.reference}/respond`, fd); toast.success(`Response sent to ${recipient}`); onSent(); }
        catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    }

    return (
        <Modal title={`Respond to Inquirer — ${record.reference}`} onClose={onClose} wide testid="respond-composer">
            <div className="text-xs text-lnl-gray mb-3">To: <b className="text-lnl-charcoal" data-testid="respond-to">{recipient}</b> · Editing here never changes the saved template.</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Start from a template (optional)">
                    <select value={tplId} onChange={(e) => applyTemplate(e.target.value)} className={inputCls} data-testid="respond-template">
                        <option value="">Write a custom response</option>{templates.map((t) => <option key={t.id} value={t.id}>{t.template_name}</option>)}
                    </select>
                </Field>
                <Field label="Signature">
                    <select value={sig} onChange={(e) => setSig(e.target.value)} className={inputCls} data-testid="respond-signature">
                        <option value="">No signature</option>{sigs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </Field>
                <div className="md:col-span-2"><Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="respond-subject" /></Field></div>
                <div className="md:col-span-2"><RichTextEditor key={editorKey} value={body} onChange={setBody} variables={meta?.variables || []} testid="respond-body" /></div>
            </div>
            <div className="mt-3">
                <label className="lnl-btn-secondary cursor-pointer inline-flex" data-testid="respond-attach-label"><Paperclip className="h-4 w-4" /> Add attachments
                    <input type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="respond-attach-input" />
                </label>
                <ul className="mt-2 space-y-1 text-xs">{files.map((f, i) => <li key={i} className="flex items-center gap-2"><Paperclip className="h-3 w-3" />{f.name} <span className="text-lnl-gray">({(f.size / 1024).toFixed(0)} KB)</span><button onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove attachment"><X className="h-3 w-3" /></button></li>)}</ul>
                <p className="text-[11px] text-lnl-gray mt-1">Up to 10 MB total. Files are delivered as secure download links.</p>
            </div>
            <div className="mt-4 flex justify-end"><button onClick={send} disabled={busy || !subject.trim() || !body.replace(/<[^>]+>/g, "").trim()} className="lnl-btn-primary" data-testid="respond-send"><Send className="h-4 w-4" /> {busy ? "Sending…" : "Send Response"}</button></div>
        </Modal>
    );
}
